// Bridges the app-wide agent-jobs feed (AgentContext `tasks`) to in-app browser
// notifications. Mounted once at the app layout so it runs across every staff
// view while the tab is open.
//
// There is no global app-wide *findings* feed in the web app — detector finding
// feeds are per-page. The one activity source polled app-wide is the composed
// AgentTask list in `AgentContext` (the same feed the sidebar AgentBadge reads,
// polled every 2s while a task is in flight). So we notify on agent-job
// lifecycle *completion events*: a task reaching `failed`, `review_required`,
// or `completed`. Each event is mapped to a notification candidate (agent
// family + severity) and run through the SAME prefs gate the email digest used
// (`passesNotificationPrefs`), so the existing per-family + severity Settings
// toggles govern what surfaces.
//
// Already-notified task ids are tracked in a localStorage-backed seen-set so a
// completion is announced once per device, never re-announced on the next poll
// or a page reload.

import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import type {
    AgentTask,
    AgentTaskType,
    NotificationPrefsDto,
    SeverityFilter,
} from '@septima/contract';
import { api } from '@/lib/api';
import { useAgent } from '@/contexts/AgentContext';
import { useSessionQuery } from '@/lib/auth';
import { AGENTS } from '@/components/agent/agent-kanban-meta';
import {
    useWebNotifications,
    passesNotificationPrefs,
    type NotificationCandidate,
} from '@/lib/use-web-notifications';

const SEEN_KEY = 'septima.webNotifications.seenTaskIds';
const SEEN_CAP = 300;

// Task statuses that constitute a notify-worthy completion event.
type TerminalStatus = 'failed' | 'review_required' | 'completed';
function isTerminalEvent(status: string): status is TerminalStatus {
    return status === 'failed' || status === 'review_required' || status === 'completed';
}

// Map an AgentTask completion to its severity tier (same three-tier scale the
// digest uses): a failure is critical, a run awaiting human review is
// attention, a clean completion is info.
function severityForStatus(status: TerminalStatus): SeverityFilter {
    if (status === 'failed') return 'critical';
    if (status === 'review_required') return 'attention';
    return 'info';
}

// Resolve an AgentTaskType to its owning agent family code (AEGIS, PULSE, …)
// using the kanban metadata registry — the one place that mapping lives.
const FAMILY_BY_TASK_TYPE: Record<string, string> = (() => {
    const map: Record<string, string> = {};
    for (const agent of AGENTS) {
        for (const t of agent.taskTypes) map[t] = agent.code;
    }
    return map;
})();

function agentFamilyForTask(type: AgentTaskType): string {
    return FAMILY_BY_TASK_TYPE[type] ?? 'AGENT';
}

function candidateForTask(task: AgentTask): NotificationCandidate | null {
    if (!isTerminalEvent(task.status)) return null;
    return {
        agentName: agentFamilyForTask(task.type),
        severity: severityForStatus(task.status),
    };
}

function titleForTask(task: AgentTask): string {
    const family = agentFamilyForTask(task.type);
    if (task.status === 'failed') return `Agent ${family} failed`;
    if (task.status === 'review_required') return `Agent ${family} — needs review`;
    return `Agent ${family} finished`;
}

function bodyForTask(task: AgentTask): string {
    return task.clientName ? `${task.triggerDetail} · ${task.clientName}` : task.triggerDetail;
}

function loadSeen(): Set<string> {
    try {
        const raw = window.localStorage.getItem(SEEN_KEY);
        if (!raw) return new Set();
        const parsed: unknown = JSON.parse(raw);
        return Array.isArray(parsed)
            ? new Set(parsed.filter((x): x is string => typeof x === 'string'))
            : new Set();
    } catch {
        return new Set();
    }
}

function persistSeen(seen: ReadonlySet<string>): void {
    try {
        const arr = Array.from(seen);
        const capped = arr.length > SEEN_CAP ? arr.slice(arr.length - SEEN_CAP) : arr;
        window.localStorage.setItem(SEEN_KEY, JSON.stringify(capped));
    } catch {
        // Private mode / quota — degrade to per-session dedupe.
    }
}

/**
 * Mount once at the app shell. While web notifications are enabled on this
 * device, fires a browser notification for each newly-terminal agent job that
 * passes the user's notification preferences.
 */
export function useAgentWebNotifications(): void {
    const { notify, enabled } = useWebNotifications();
    const { tasks } = useAgent();
    const { data: session } = useSessionQuery();
    const isAuthenticated = session?.authenticated ?? false;

    const { data: prefs } = useQuery<NotificationPrefsDto>({
        queryKey: ['user-prefs', 'notifications'],
        queryFn: async () => {
            const res = await api.userPrefs.getNotificationPrefs();
            if (res.status !== 200) throw new Error('Failed to load notification preferences');
            return res.body;
        },
        enabled: isAuthenticated && enabled,
        staleTime: 60_000,
    });

    // Seen ids persist across reloads so a completion is announced once. Held in
    // a ref (not state) so updating it never re-renders the host layout.
    const seenRef = useRef<Set<string> | null>(null);
    if (seenRef.current === null) seenRef.current = loadSeen();

    // On first run while disabled, prime the seen-set with everything already
    // terminal so enabling later doesn't dump a backlog of historical jobs.
    const primedRef = useRef(false);

    useEffect(() => {
        const seen = seenRef.current;
        if (!seen) return;

        // While disabled, just keep the seen-set current so a later enable
        // doesn't replay history; never notify.
        if (!enabled || !isAuthenticated) {
            for (const task of tasks) {
                if (isTerminalEvent(task.status)) seen.add(task.id);
            }
            persistSeen(seen);
            primedRef.current = true;
            return;
        }

        // First enabled pass: prime without notifying, so we only announce
        // genuinely-new completions from here on.
        if (!primedRef.current) {
            for (const task of tasks) {
                if (isTerminalEvent(task.status)) seen.add(task.id);
            }
            persistSeen(seen);
            primedRef.current = true;
            return;
        }

        let changed = false;
        for (const task of tasks) {
            if (!isTerminalEvent(task.status)) continue;
            if (seen.has(task.id)) continue;
            seen.add(task.id);
            changed = true;
            const candidate = candidateForTask(task);
            if (candidate && passesNotificationPrefs(candidate, prefs)) {
                notify({
                    title: titleForTask(task),
                    body: bodyForTask(task),
                    tag: `agent-job-${task.id}`,
                });
            }
        }
        if (changed) persistSeen(seen);
    }, [tasks, enabled, isAuthenticated, prefs, notify]);
}
